import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import useConfirm from "@/hooks/useConfirm";
import { clientsKeys } from "@/query-keys";
import { type ClientListItem } from "@/schemas/clientSchema";
import { services } from "@/services";
import useClientsQuery from "./_hooks/useClientsQuery";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import CreateClientForm from "./_components/CreateClientForm";
import useColumns from "./_hooks/useColumns";
import useClientMutation from "./_hooks/useClientMutation";

export const Route = createFileRoute("/_private/clients/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: clientsKeys.lists(deps.q),
      queryFn: () => services.getClients(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = useClientsQuery(search.q, loaderData);

  const columns = useColumns();
  const { deleteClient, isDeleting } = useClientMutation();

  const deleteConfirm = useConfirm({
    title: t("clients.confirm.delete.title"),
    description: t("clients.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (row: ClientListItem) => navigate({ to: "/clients/$clientId", params: { clientId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: ClientListItem) => {
      deleteConfirm.confirm(() => {
        deleteClient(row.id);
      });
    },
    [deleteConfirm, deleteClient],
  );

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader
        title={t("clients.list.title")}
        subtitle={t("clients.list.subtitle")}
        action={<CreateClientForm />}
      />

      <DataTable<ClientListItem>
        searchPlaceholder={t("clients.list.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={clientsKeys.lists(search.q)}
        navigateFrom="/clients"
        searchFrom="/_private/clients/"
        onView={handleView}
        onDelete={handleDelete}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
